import type { PlanType } from '../store/funnelV3Store';

export const AB_DEFAULT_PLAN_STORAGE_KEY = 'mt_ab_default_plan';

export type AbDefaultPlanVariant = 'control' | 'twelve_default';

const VALID_VARIANTS: readonly AbDefaultPlanVariant[] = ['control', 'twelve_default'];

const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

function isValidVariant(value: string): value is AbDefaultPlanVariant {
    return (VALID_VARIANTS as readonly string[]).includes(value);
}

function readCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${AB_DEFAULT_PLAN_STORAGE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(variant: AbDefaultPlanVariant): void {
    if (typeof document === 'undefined') return;
    document.cookie = `${AB_DEFAULT_PLAN_STORAGE_KEY}=${encodeURIComponent(variant)};path=/;max-age=${COOKIE_MAX_AGE_SEC};SameSite=Lax`;
}

function readPersistedVariant(): AbDefaultPlanVariant | null {
    if (typeof localStorage !== 'undefined') {
        const fromStorage = localStorage.getItem(AB_DEFAULT_PLAN_STORAGE_KEY);
        if (fromStorage && isValidVariant(fromStorage)) {
            return fromStorage;
        }
    }

    const fromCookie = readCookie();
    if (fromCookie && isValidVariant(fromCookie)) {
        return fromCookie;
    }

    return null;
}

function persistVariant(variant: AbDefaultPlanVariant): void {
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AB_DEFAULT_PLAN_STORAGE_KEY, variant);
    }
    writeCookie(variant);
}

/**
 * Read the sticky A/B variant without assigning. Returns null if the visitor
 * has not entered the paywall yet (ensureAbDefaultPlanVariant not called).
 */
export function getAbDefaultPlanVariant(): AbDefaultPlanVariant | null {
    return readPersistedVariant();
}

/**
 * Assign a 50/50 variant on first paywall entry. Reuses localStorage/cookie if present.
 */
export function ensureAbDefaultPlanVariant(): AbDefaultPlanVariant {
    const existing = readPersistedVariant();
    if (existing) {
        return existing;
    }

    const variant: AbDefaultPlanVariant = Math.random() < 0.5 ? 'control' : 'twelve_default';
    persistVariant(variant);
    return variant;
}

export function getDefaultPlanTypeForVariant(variant: AbDefaultPlanVariant): PlanType {
    return variant === 'twelve_default' ? '12_week' : '4_week';
}
